/**
 * CAPA DE DATOS (DL): CARDS / DISCIPLINA
 * ------------------------------------------------------------------
 * Maneja las peticiones HTTP puras hacia los endpoints de control 
 * disciplinario del partido activo.
 * ------------------------------------------------------------------
 */

/**
 * Obtiene el estado disciplinario actual del partido (jugadores amonestados/expulsados).
 * @returns {Promise<{success: boolean, data?: any, message?: string}>}
 */
const GetCardsState = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}Cards/state`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'No fue posible obtener el estado disciplinario.' };
        }
        
        const responseData = await response.json();
        return { success: true, data: responseData };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar el estado de las tarjetas.' };
    }
};

/**
 * Registra una amonestación (amarilla) o expulsión (roja) en el servidor.
 * @param {Object} cardPayload - { playerId, teamType, yellowCardsCount, isRedCarded, matchMinute, infractionTitle }
 * @returns {Promise<{success: boolean, data?: any, message?: string}>}
 */
const ApplyCard = async (cardPayload) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}Cards/apply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cardPayload)
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'No se pudo aplicar la tarjeta al jugador.' };
        }
        
        const responseData = await response.json();
        return { success: true, data: responseData.data };
    } catch (error) {
        return { success: false, message: 'Error de conexión al registrar la tarjeta en el servidor.' };
    }
};

/**
 * Borra el historial disciplinario del partido actual para comenzar un nuevo juego desde cero.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const ResetMatchCards = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}Cards/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'No fue posible reiniciar las tarjetas del partido.' };
        }
        
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de red al intentar reiniciar el estado disciplinario.' };
    }
};

/**
 * Envía un comando de disparo directo (como close o show_board) al overlay de tarjetas.
 * @param {string} action - 'close' | 'show_board' | 'hide_board'
 * @param {number|null} playerId - ID del jugador (opcional)
 */
const TriggerCardsOverlay = async (action, playerId = null) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}Cards/trigger`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action, playerId })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'No fue posible disparar la acción del overlay.' };
        }
        
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de red al disparar comando al overlay.' };
    }
};

export { GetCardsState, ApplyCard, ResetMatchCards, TriggerCardsOverlay };